'use client';

import clsx from 'clsx';
import type { ForwardedRef, ReactNode } from 'react';
import { Tag as RACTag, type TagProps as RACTagProps, Button as RACButton } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { IconCloseBold } from '../../icons/index.js';
import { withStyleProps } from '../../with-style-props.js';
import type { StringLikeChildren } from '../types.js';
import type { CommonProps } from '../types.js';

export interface TagProps extends Omit<CommonProps, 'id'>, Pick<RACTagProps, 'id' | 'isDisabled'> {
  /** The content to display as the label. */
  children: StringLikeChildren;
  /** The icon to display at the start of the tag. */
  iconStart?: ReactNode;
}

function Tag(
  { children, iconStart, UNSAFE_className, UNSAFE_style, ...props }: TagProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const textValue =
    typeof children === 'string' ? children : typeof children === 'number' ? String(children) : children.join('');

  return (
    <RACTag
      {...props}
      ref={ref}
      className={clsx('cim-tag', UNSAFE_className)}
      style={UNSAFE_style}
      textValue={textValue}
    >
      {({ allowsRemoving }) => (
        <>
          {iconStart && <div className="cim-tag-icon">{iconStart}</div>}
          <span className="cim-tag-text">{children}</span>
          {allowsRemoving && (
            <RACButton slot="remove" excludeFromTabOrder>
              <IconCloseBold />
            </RACButton>
          )}
        </>
      )}
    </RACTag>
  );
}

/**
 * Displays a single tag within a tag group.
 *
 * See [tag group usage guidelines](https://ui.cimpress.io/components/tag-group/).
 */
const _Tag = withStyleProps(forwardRef(Tag), 'Tag');

export { _Tag as Tag };
